import { useMemo } from "react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { useDevices } from "../providers/DevicesProvider";

/**
 * Hook that provides browser compatibility information for video features
 * Uses AWS Chime SDK's built-in browser detection and device availability
 */
export function useBrowserCompatibilityFeatures() {
  const { audioInputDevices, videoInputDevices, isDevicesLoading } =
    useDevices();

  return useMemo(() => {
    const behavior = new DefaultBrowserBehavior();

    // Check if devices are available
    const hasAudioDevices = audioInputDevices.length > 0;
    const hasVideoDevices = videoInputDevices.length > 0;

    // Basic compatibility
    const isSupported = behavior.isSupported();

    // Use Chime SDK for browser detection, with regex for mobile
    const userAgent = typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : 'unknown';
    const isMobile = /mobi|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

    // Detect tablets separately for screen share support
    const isTablet = /iPad/i.test(userAgent) ||
      ((/Android/i.test(userAgent) && !/Mobile/i.test(userAgent))) ||
      (navigator.maxTouchPoints > 1 && Math.min(screen.width, screen.height) >= 600);

    const browserName = behavior.name();

    // Feature support - combines browser support AND device availability
    const supportsAudio = behavior.isSupported() && hasAudioDevices;
    const supportsVideo = behavior.isSupported() && hasVideoDevices;
    
    // Screen sharing: enable for desktop and tablets (tablets support getDisplayMedia)
    const supportsScreenShare = (!isMobile || isTablet) && behavior.isSupported() && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

    // Debug screen share support

    const supportsSimulcast = behavior.isSimulcastSupported();
    const supportsBackgroundFilter = behavior.supportsBackgroundFilter();
    const supportsAudioOutput = behavior.supportsSetSinkId();
    const supportsVideoFx = behavior.isVideoFxSupportedBrowser();

    // Browser info
    const browserVersion = behavior.version();
    const supportString = behavior.supportString();

    // A user is considered supported if browser and audio are supported and available
    // While devices are still loading, optimistically assume supported to prevent flash of "not supported" message
    const isUserSupported = isDevicesLoading ? true : isSupported && supportsAudio;

    const supportObject = {
      isDevicesLoading,
      isUserSupported,
      isSupported,
      supportsAudio,
      supportsVideo,
      supportsScreenShare,
      supportsSimulcast,
      supportsBackgroundFilter,
      supportsAudioOutput,
      supportsVideoFx,
      hasAudioDevices,
      hasVideoDevices,
      isMobile,
      isTablet,
      browserName,
      browserVersion,
      supportString,
    };

    return supportObject;
  }, [audioInputDevices.length, videoInputDevices.length, isDevicesLoading]);
}
